/**
 * Pledge Review Hooks
 *
 * React Query hooks for fetching and managing consumer pledge proposals.
 * Pledges originate in the consumer domain; this admin view reads cross-domain
 * projections via GET /api/admin/pledges.
 *
 * Types derived from platform-admin.openapi.yaml via @aaimplatform/institutional-types.
 */

import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { platformAdminCanonicalClient, platformAdminClient } from '../api/client';
import type { components } from '@aaimplatform/institutional-types/platform-admin';

// ---------------------------------------------------------------------------
// Type aliases from generated OpenAPI schema
// ---------------------------------------------------------------------------

export type PledgeStatus = components['schemas']['PledgeStatus'];
export type PledgeSummary = components['schemas']['PledgeSummary'];
export type PledgeDetail = components['schemas']['PledgeDetail'];
export type PledgeListResponse = components['schemas']['PledgeListResponse'];
export type PledgeDetailResponse = components['schemas']['PledgeDetailResponse'];
export type PledgeAdmissibleAction = components['schemas']['PledgeAdmissibleAction'];
export type PledgeSelectedAccountDetail = components['schemas']['PledgeSelectedAccountDetail'];
export type PledgeSelectedHoldingDetail = components['schemas']['PledgeSelectedHoldingDetail'];
export type PledgeConsentProof = components['schemas']['PledgeConsentProof'];
export type PledgeMemberSignature = components['schemas']['PledgeMemberSignature'];
export type AcceptPledgeRequest = components['schemas']['AcceptPledgeRequest'];
export type DeclinePledgeRequest = components['schemas']['DeclinePledgeRequest'];
export type ReturnPledgeRequest = components['schemas']['ReturnPledgeRequest'];
export type ReleasePledgeRequest = components['schemas']['ReleasePledgeRequest'];
export type DenyPledgeReleaseRequestRequest = components['schemas']['DenyPledgeReleaseRequestRequest'];

// ---------------------------------------------------------------------------
// Query key constants
// ---------------------------------------------------------------------------

const PLEDGES_KEY = 'pledges';

// ---------------------------------------------------------------------------
// API helpers -- thin wrappers over the typed openapi-fetch client
// ---------------------------------------------------------------------------

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    // The HTTP status rides on the thrown Error, so a panel can word a refusal
    // for the reader rather than print the server's internal message (a 403
    // on release read "Pledge review requires platform_admin role", fi-g1-1).
    throw Object.assign(new Error(errorBody?.message ?? `HTTP ${status}: ${statusText}`), { status });
  }
  return result.data;
}

const pledgesApi = {
  list: async (params?: {
    status?: PledgeStatus;
    page?: number;
    limit?: number;
  }): Promise<PledgeListResponse> => {
    const result = await platformAdminClient.GET('/pledges', {
      params: {
        query: {
          status: params?.status,
          page: params?.page,
          limit: params?.limit,
        },
      },
    });
    return unwrap(result);
  },

  get: async (pledgeId: string): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.GET('/pledges/{pledgeId}', {
      params: { path: { pledgeId } },
    });
    return unwrap(result);
  },

  acknowledge: async (
    pledgeId: string,
    body?: { notes?: string },
  ): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.PATCH('/pledges/{pledgeId}/acknowledge', {
      params: { path: { pledgeId } },
      body: body ?? {},
    });
    return unwrap(result);
  },

  accept: async (
    pledgeId: string,
    body: AcceptPledgeRequest,
    stepUpToken?: string,
  ): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.POST('/pledges/{pledgeId}/accept', {
      params: {
        path: { pledgeId },
        header: stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined,
      },
      body,
    });
    return unwrap(result);
  },

  decline: async (
    pledgeId: string,
    body: DeclinePledgeRequest,
    stepUpToken?: string,
  ): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.POST('/pledges/{pledgeId}/decline', {
      params: {
        path: { pledgeId },
        header: stepUpToken ? { 'X-Step-Up-Token': stepUpToken } : undefined,
      },
      body,
    });
    return unwrap(result);
  },

  return: async (
    pledgeId: string,
    body: ReturnPledgeRequest,
  ): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.POST('/pledges/{pledgeId}/return', {
      params: { path: { pledgeId } },
      body,
    });
    return unwrap(result);
  },

  release: async (
    pledgeId: string,
    body: ReleasePledgeRequest,
  ): Promise<PledgeDetailResponse> => {
    const result = await platformAdminClient.POST('/pledges/{pledgeId}/release', {
      params: { path: { pledgeId } },
      body,
    });
    return unwrap(result);
  },

  // GH #2280: deny the customer's OPEN release request; the pledge stays
  // ACTIVE. Approving the request is `release`. The route is canonical
  // (/api/v1/admin/pledges), so it goes through the origin-anchored client.
  denyReleaseRequest: async (
    pledgeId: string,
    body: DenyPledgeReleaseRequestRequest,
  ): Promise<PledgeDetailResponse> => {
    const result = await platformAdminCanonicalClient.POST('/api/v1/admin/pledges/{pledgeId}/release-request/deny', {
      params: { path: { pledgeId } },
      body,
    });
    return unwrap(result);
  },
};

/**
 * Invalidates the pledge list and the one pledge's detail. Resolves once the
 * active queries have refetched.
 */
function invalidatePledge(queryClient: QueryClient, pledgeId: string): Promise<unknown> {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: [PLEDGES_KEY, 'list'] }),
    queryClient.invalidateQueries({ queryKey: [PLEDGES_KEY, 'detail', pledgeId] }),
  ]);
}

/**
 * A 409 PLEDGE_STATE_CONFLICT means the pledge changed while the request was in
 * flight (GH #2572, #2589), and the server message tells the operator to reload.
 * The SPA does the reload: it refetches before the error state is set, so the
 * message renders over the pledge's current state and admissible actions rather
 * than the read that lost the race (GH #2592). React Query awaits a promise
 * returned from `onError`. Other statuses leave the cache alone.
 */
export function refetchOnConflict(
  queryClient: QueryClient,
  error: Error,
  pledgeId: string,
): Promise<unknown> | undefined {
  if ((error as { status?: unknown }).status !== 409) return undefined;
  return invalidatePledge(queryClient, pledgeId);
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Hook for listing pledge proposals with optional filtering and pagination.
 */
export function usePledges(params?: {
  status?: PledgeStatus;
  page?: number;
  limit?: number;
}) {
  return useQuery<PledgeListResponse, Error>({
    queryKey: [PLEDGES_KEY, 'list', params],
    queryFn: () => pledgesApi.list(params),
  });
}

/**
 * Hook for fetching a single pledge with full account and consent detail.
 * Only executes when pledgeId is defined.
 */
export function usePledgeDetail(pledgeId: string | undefined) {
  return useQuery<PledgeDetailResponse, Error>({
    queryKey: [PLEDGES_KEY, 'detail', pledgeId],
    queryFn: () => pledgesApi.get(pledgeId as string),
    enabled: !!pledgeId,
  });
}

/**
 * Mutation hook for acknowledging a PENDING pledge.
 * Invalidates both the list and the specific pledge detail on success, and on a
 * 409 state conflict (see refetchOnConflict).
 */
export function useAcknowledgePledge() {
  const queryClient = useQueryClient();

  return useMutation<
    PledgeDetailResponse,
    Error,
    { pledgeId: string; notes?: string }
  >({
    mutationFn: ({ pledgeId, notes }) => pledgesApi.acknowledge(pledgeId, { notes }),
    onSuccess: (_data, variables) => {
      void invalidatePledge(queryClient, variables.pledgeId);
    },
    onError: (error, variables) => refetchOnConflict(queryClient, error, variables.pledgeId),
  });
}

/**
 * Mutation hook for accepting an ACKNOWLEDGED pledge (transition to ACTIVE).
 */
export function useAcceptPledge() {
  const queryClient = useQueryClient();

  return useMutation<
    PledgeDetailResponse,
    Error,
    { pledgeId: string; body: AcceptPledgeRequest; stepUpToken?: string }
  >({
    mutationFn: ({ pledgeId, body, stepUpToken }) => pledgesApi.accept(pledgeId, body, stepUpToken),
    onSuccess: (_data, variables) => {
      void invalidatePledge(queryClient, variables.pledgeId);
    },
    onError: (error, variables) => refetchOnConflict(queryClient, error, variables.pledgeId),
  });
}

/**
 * Mutation hook for declining a PENDING or ACKNOWLEDGED pledge.
 */
export function useDeclinePledge() {
  const queryClient = useQueryClient();

  return useMutation<
    PledgeDetailResponse,
    Error,
    { pledgeId: string; body: DeclinePledgeRequest; stepUpToken?: string }
  >({
    mutationFn: ({ pledgeId, body, stepUpToken }) => pledgesApi.decline(pledgeId, body, stepUpToken),
    onSuccess: (_data, variables) => {
      void invalidatePledge(queryClient, variables.pledgeId);
    },
    onError: (error, variables) => refetchOnConflict(queryClient, error, variables.pledgeId),
  });
}

/**
 * Mutation hook for returning a PENDING or ACKNOWLEDGED pledge for revision.
 */
export function useReturnPledge() {
  const queryClient = useQueryClient();

  return useMutation<
    PledgeDetailResponse,
    Error,
    { pledgeId: string; body: ReturnPledgeRequest }
  >({
    mutationFn: ({ pledgeId, body }) => pledgesApi.return(pledgeId, body),
    onSuccess: (_data, variables) => {
      void invalidatePledge(queryClient, variables.pledgeId);
    },
    onError: (error, variables) => refetchOnConflict(queryClient, error, variables.pledgeId),
  });
}

/**
 * Mutation hook for releasing an ACTIVE pledge.
 */
/**
 * GH #2280: deny the customer's release request. Same cache handling as the
 * other decisions, including the refetch on a 409 state conflict.
 */
export function useDenyPledgeReleaseRequest() {
  const queryClient = useQueryClient();

  return useMutation<
    PledgeDetailResponse,
    Error,
    { pledgeId: string; body: DenyPledgeReleaseRequestRequest }
  >({
    mutationFn: ({ pledgeId, body }) => pledgesApi.denyReleaseRequest(pledgeId, body),
    onSuccess: (_data, variables) => {
      void invalidatePledge(queryClient, variables.pledgeId);
    },
    onError: (error, variables) => refetchOnConflict(queryClient, error, variables.pledgeId),
  });
}

export function useReleasePledge() {
  const queryClient = useQueryClient();

  return useMutation<
    PledgeDetailResponse,
    Error,
    { pledgeId: string; body: ReleasePledgeRequest }
  >({
    mutationFn: ({ pledgeId, body }) => pledgesApi.release(pledgeId, body),
    onSuccess: (_data, variables) => {
      void invalidatePledge(queryClient, variables.pledgeId);
    },
    onError: (error, variables) => refetchOnConflict(queryClient, error, variables.pledgeId),
  });
}
